import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  Plus,
  Search,
  Filter,
  Play,
  Copy,
  Archive,
  CheckCircle2,
  Clock,
  AlertTriangle,
  History,
  Layers,
  FileText,
  RefreshCw,
  MoreVertical,
  Sliders,
  ChevronRight,
  ChevronDown,
  Pause,
  RotateCcw,
  Ban,
  Eye,
  Loader2,
} from 'lucide-react';
import {
  AutomationBatchExecution,
  AutomationDefinition,
  AutomationRun,
} from '../../../types/automation';
import { AutomationCanvas } from './AutomationCanvas';
import { AutomationRunDetailModal } from './AutomationRunDetailModal';
import {
  DraftSaveCoordinator,
  DraftSaveStatus,
  DraftSnapshot,
  draftSnapshotKey,
} from './automationDraftSave';
import { safeFetch } from '../../../lib/api';
import { safePiiText, sanitizeEncryptedPiiBlocks } from '../../../lib/piiDisplay';
import { confirmDialog } from '../../../lib/confirmStore';
import { notifyToast } from '../../../lib/toastStore';

// S12: human age for the oldest waiting job (e.g. "3h 12m").
export function formatWaitingAge(ageMs: number | null | undefined): string {
  if (ageMs == null || !Number.isFinite(ageMs)) return '—';
  const totalMinutes = Math.floor(ageMs / 60000);
  if (totalMinutes >= 24 * 60) {
    const d = Math.floor(totalMinutes / (24 * 60));
    const h = Math.floor((totalMinutes % (24 * 60)) / 60);
    return `${d}d ${h}h`;
  }
  if (totalMinutes >= 60) return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
  if (totalMinutes >= 1) return `${totalMinutes}m`;
  return '<1m';
}

// S12: pass duration (e.g. "842ms", "1.8s").
export function formatPassDuration(durationMs: number | null | undefined): string {
  if (durationMs == null || !Number.isFinite(durationMs)) return '—';
  if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
  return `${(durationMs / 1000).toFixed(1)}s`;
}

export const AutomationManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'library' | 'builder' | 'history'>('library');
  const [automations, setAutomations] = useState<AutomationDefinition[]>([]);
  const [runs, setRuns] = useState<AutomationRun[]>([]);
  const [loading, setLoading] = useState(true);
  // Load failures must be visible: a failed list fetch is NOT the same as an
  // empty workspace (previously 401/404 rendered identically to "no flows").
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Builder State
  const [editingAutomation, setEditingAutomation] = useState<AutomationDefinition | null>(null);
  const [autoName, setAutoName] = useState('New Recruitment Flow');
  const [autoDesc, setAutoDesc] = useState('');
  const [autoCategory, setAutoCategory] = useState('Onboarding');
  const [nodes, setNodes] = useState<any[]>([]);
  const [edges, setEdges] = useState<any[]>([]);
  // Execution settings (I-5): these are now WIRED — the engine reads
  // them at run creation. maxRetries is the per-step attempt ceiling;
  // errorHandling decides what a terminal step failure does to the run.
  const [autoMaxRetries, setAutoMaxRetries] = useState(3);
  const [autoErrorHandling, setAutoErrorHandling] = useState<'pause_flow' | 'skip_failed_step' | 'cancel_flow'>('pause_flow');
  // Publish/simulate feedback (I-1/I-10): validation errors AND
  // warnings render here — warnings used to be computed server-side
  // and thrown away, so deliberate-but-sharp configurations (stage
  // moves without gate checks, fixed email recipients) published
  // silently.
  const [publishFeedback, setPublishFeedback] = useState<{
    kind: 'error' | 'warning' | 'success';
    title: string;
    messages: string[];
  } | null>(null);
  const [simulation, setSimulation] = useState<any | null>(null);
  const [simulating, setSimulating] = useState(false);

  // Auto Save: the draft-save coordinator is the SINGLE path for draft
  // persistence (auto save, manual Save Draft, Retry, and the
  // publish/simulate pre-save all flush through it — one request in
  // flight, latest snapshot wins; see automationDraftSave.ts).
  const [saveStatus, setSaveStatus] = useState<DraftSaveStatus>('saved');
  // Incremented every time the builder is (re)anchored to a flow
  // (Edit / Create New). A create response is adopted into
  // `editingAutomation` only while its session is still current, so a
  // late save for a flow the user already left cannot relabel the
  // builder (the coordinator still tracks that flow's id itself).
  const builderSessionRef = useRef(0);

  const persistDraftSnapshot = useCallback(async (snap: DraftSnapshot): Promise<string> => {
    const payload = {
      name: snap.name,
      description: snap.description,
      category: snap.category,
      nodes: snap.nodes,
      edges: snap.edges,
      executionSettings: snap.executionSettings,
    };
    if (!snap.automationId) {
      const createData = await safeFetch('/api/admin/automations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (builderSessionRef.current === snap.sessionId) {
        setEditingAutomation(createData.automation);
      }
      return createData.automation.id;
    }
    const updated = await safeFetch(`/api/admin/automations/${snap.automationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    // Re-anchor the builder to the server's canonical record (the PUT
    // response carries { id, ...doc, ...updates }). Without this the
    // builder kept the pre-save object while the library kept its own
    // stale copy — a rename saved here never reached either.
    if (builderSessionRef.current === snap.sessionId && updated?.automation) {
      setEditingAutomation(updated.automation);
    }
    return snap.automationId;
  }, []);

  const [saveCoordinator] = useState(() => {
    const coordinator = new DraftSaveCoordinator({
      save: (snap) => persistDraftSnapshot(snap),
      onStatus: (status) => setSaveStatus(status),
      // The library renders from the `automations` state and Edit Flow
      // reopens from those same objects, so a persisted snapshot must
      // be written back into that record the moment it saves — this
      // hook existed in the coordinator for exactly that purpose but
      // was never wired, which is why an auto-saved rename left the
      // library card (and the object Edit Flow reopens) showing the
      // pre-save name until a full list refetch happened to land. The
      // snapshot IS the draft record the server just stored (same
      // `name` field the list GET reads), so this is the single
      // source flowing into the list, not a second write.
      onSaved: (snap, id) => {
        setAutomations((prev) =>
          prev.map((a) =>
            a.id === id
              ? {
                  ...a,
                  name: snap.name,
                  description: snap.description,
                  category: snap.category,
                  nodes: snap.nodes as AutomationDefinition['nodes'],
                  edges: snap.edges as AutomationDefinition['edges'],
                  executionSettings: snap.executionSettings as AutomationDefinition['executionSettings'],
                }
              : a
          )
        );
      },
    });
    // The builder's initial state IS a brand-new unsaved draft; anchor
    // it as the baseline so opening the editor fires no save.
    coordinator.reset({
      sessionId: 0,
      automationId: null,
      key: draftSnapshotKey({
        name: 'New Recruitment Flow',
        description: '',
        category: 'Onboarding',
        nodes: [],
        edges: [],
        executionSettings: { maxRetries: 3, errorHandling: 'pause_flow' },
      }),
      name: 'New Recruitment Flow',
      description: '',
      category: 'Onboarding',
      nodes: [],
      edges: [],
      executionSettings: { maxRetries: 3, errorHandling: 'pause_flow' },
    });
    return coordinator;
  });

  // Snapshot of the draft exactly as the editor currently shows it.
  const buildLiveSnapshot = (): DraftSnapshot => {
    const executionSettings = currentExecutionSettings();
    const parts = {
      name: autoName,
      description: autoDesc,
      category: autoCategory,
      nodes,
      edges,
      executionSettings,
    };
    return {
      sessionId: builderSessionRef.current,
      automationId: editingAutomation?.id ?? null,
      key: draftSnapshotKey(parts),
      ...parts,
    };
  };

  // Any draft edit (canvas graph changes via onChange -> setNodes/
  // setEdges, metadata fields, execution settings) marks the draft
  // dirty; the coordinator debounces, coalesces and persists it.
  // Snapshots equal to the persisted content are a no-op inside the
  // coordinator, so opening a flow (or a no-op edit) never saves.
  useEffect(() => {
    if (activeTab !== 'builder') return;
    saveCoordinator.markDirty(buildLiveSnapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, nodes, edges, autoName, autoDesc, autoCategory, autoMaxRetries, autoErrorHandling]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (saveStatus === 'saved') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatus]);

  useEffect(() => {
    return () => saveCoordinator.dispose();
  }, [saveCoordinator]);
  // Run history hygiene (I-7)
  const [runStatusFilter, setRunStatusFilter] = useState<string>('all');

  // Selected Run Detail Modal
  const [selectedRun, setSelectedRun] = useState<AutomationRun | null>(null);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);

  // Slice 7: the batch layer of Execution History — cursor-paginated
  // batch list + drill-down into a batch's runs.
  const [batches, setBatches] = useState<AutomationBatchExecution[]>([]);
  const [batchesNextCursor, setBatchesNextCursor] = useState<string | null>(null);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [batchRuns, setBatchRuns] = useState<AutomationRun[]>([]);
  const [batchRunsNextCursor, setBatchRunsNextCursor] = useState<string | null>(null);
  const [runsNextCursor, setRunsNextCursor] = useState<string | null>(null);

  // Slice 8: scheduler health (dispatcher heartbeat) for the card in
  // the History tab.
  const [engineHealth, setEngineHealth] = useState<any | null>(null);

  // S12: failures (dead-letter) summary + items, and the per-recipient
  // delivery view. Both are advisory like health: a failed fetch
  // leaves the last state and never breaks the tab.
  const [failuresData, setFailuresData] = useState<any | null>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [deliveriesNextCursor, setDeliveriesNextCursor] = useState<string | null>(null);

  useEffect(() => {
    fetchAutomations();
    fetchRuns();
    fetchFailures();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchBatches();
      fetchRuns();
      fetchHealth();
      fetchFailures();
      fetchDeliveries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Slice 8: 5-second polling while the History tab is open (the Bulk
  // Jobs dashboard precedent) — first pages refresh in place.
  useEffect(() => {
    if (activeTab !== 'history') return;
    const t = setInterval(() => {
      fetchRuns();
      fetchBatches();
      fetchHealth();
      fetchFailures();
      fetchDeliveries();
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, runStatusFilter]);

  const fetchAutomations = async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/admin/automations');
      setAutomations(data.automations || []);
      setLoadError(null);
    } catch (err: any) {
      console.error('Failed to fetch automations:', err);
      setLoadError(err.message || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  const fetchRuns = async (opts?: { append?: boolean; cursor?: string; status?: string }) => {
    try {
      // Slice 7: the status filter is passed to the server (it was
      // accepted there all along) and pages come back cursor-based.
      const params = new URLSearchParams({ limit: '50' });
      const status = opts?.status ?? runStatusFilter;
      if (status && status !== 'all') params.set('status', status);
      if (opts?.append && opts.cursor) params.set('cursor', opts.cursor);
      const data = await safeFetch(`/api/admin/automations/runs?${params.toString()}`);
      // Stored run records can carry encrypted PII envelopes in
      // applicant fields (legacy runs store applicantEmail as an
      // envelope map); sanitize before the records reach render so an
      // envelope can never be rendered as a React child (#31).
      const cleanRuns = (data.runs || []).map((r: any) => sanitizeEncryptedPiiBlocks(r));
      setRuns((prev) => (opts?.append ? [...prev, ...cleanRuns] : cleanRuns));
      setRunsNextCursor(data.nextCursor || null);
      setRunsError(null);
    } catch (err: any) {
      console.error('Failed to fetch runs:', err);
      setRunsError(err.message || 'Failed to load automation runs');
    }
  };

  const fetchHealth = async () => {
    try {
      const data = await safeFetch('/api/admin/automations/health');
      setEngineHealth(data.health || null);
    } catch {
      // Health is advisory; a failed read leaves the last card state.
    }
  };

  const fetchFailures = async () => {
    try {
      const data = await safeFetch('/api/admin/automations/failures?limit=50');
      if (data && data.summary) setFailuresData(sanitizeEncryptedPiiBlocks(data));
    } catch {
      // Advisory like fetchHealth: leave the last state.
    }
  };

  const fetchDeliveries = async (opts?: { append?: boolean; cursor?: string }) => {
    try {
      const params = new URLSearchParams({ limit: '25' });
      if (opts?.append && opts.cursor) params.set('cursor', opts.cursor);
      const data = await safeFetch(`/api/admin/automations/deliveries?${params.toString()}`);
      if (data && Array.isArray(data.deliveries)) {
        const cleanDeliveries = data.deliveries.map((d: any) => sanitizeEncryptedPiiBlocks(d));
        setDeliveries((prev) => (opts?.append ? [...prev, ...cleanDeliveries] : cleanDeliveries));
        setDeliveriesNextCursor(data.nextCursor || null);
      }
    } catch {
      // Advisory: leave the last state.
    }
  };

  // S12 / D6: retry from the Needs attention panel goes through the
  // EXISTING state-guarded retry endpoints, exactly like the batch
  // retry below and the run detail modal's retry.
  const handleFailureRetry = async (item: any) => {
    try {
      if (item.kind === 'run') {
        await safeFetch(`/api/admin/automations/runs/${item.id}/retry`, { method: 'POST' });
        fetchRuns();
      } else if (item.kind === 'batch') {
        await safeFetch(`/api/admin/automations/batches/${item.id}/retry`, { method: 'POST' });
        fetchBatches();
      } else {
        return; // send_unknown items are never retried/resent (D3)
      }
      fetchFailures();
    } catch (err: any) {
      notifyToast('error', `Retry failed: ${err.message}`);
    }
  };

  // Open a run in the existing run-detail modal: fetch the full run
  // by its DOCUMENT id (the modal refetches by id on open), falling
  // back to a run already loaded in the history list.
  const openRunByDocId = async (docId: string | null | undefined, runCode?: string | null) => {
    if (docId) {
      try {
        const data = await safeFetch(`/api/admin/automations/runs/${docId}`);
        if (data?.run) {
          setSelectedRun(sanitizeEncryptedPiiBlocks(data.run));
          setIsRunModalOpen(true);
          return;
        }
      } catch {
        // fall through to the loaded-list lookup
      }
    }
    const loaded = runs.find((r: any) => r.id === docId || (runCode && r.runId === runCode));
    if (loaded) {
      setSelectedRun(loaded);
      setIsRunModalOpen(true);
    }
  };

  const fetchBatches = async (opts?: { append?: boolean; cursor?: string }) => {
    try {
      const params = new URLSearchParams({ limit: '25' });
      if (opts?.append && opts.cursor) params.set('cursor', opts.cursor);
      const data = await safeFetch(`/api/admin/automations/batches?${params.toString()}`);
      const cleanBatches = (data.batches || []).map((b: any) => sanitizeEncryptedPiiBlocks(b));
      setBatches((prev) => (opts?.append ? [...prev, ...cleanBatches] : cleanBatches));
      setBatchesNextCursor(data.nextCursor || null);
      setBatchesError(null);
    } catch (err: any) {
      console.error('Failed to fetch batches:', err);
      setBatchesError(err.message || 'Failed to load automation batches');
    }
  };

  const fetchBatchRuns = async (
    batchId: string,
    opts?: { append?: boolean; cursor?: string }
  ) => {
    const params = new URLSearchParams({ limit: '50' });
    if (opts?.append && opts.cursor) params.set('runsCursor', opts.cursor);
    const data = await safeFetch(`/api/admin/automations/batches/${batchId}?${params.toString()}`);
    const cleanBatchRuns = (data.runs || []).map((r: any) => sanitizeEncryptedPiiBlocks(r));
    setBatchRuns((prev) => (opts?.append ? [...prev, ...cleanBatchRuns] : cleanBatchRuns));
    setBatchRunsNextCursor(data.runsNextCursor || null);
  };

  const toggleBatch = (batchId: string) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      return;
    }
    setExpandedBatchId(batchId);
    setBatchRuns([]);
    setBatchRunsNextCursor(null);
    fetchBatchRuns(batchId).catch((err: any) =>
      console.error('Failed to fetch batch runs:', err)
    );
  };

  const handleBatchAction = async (
    batchId: string,
    action: 'pause' | 'resume' | 'cancel' | 'retry'
  ) => {
    try {
      await safeFetch(`/api/admin/automations/batches/${batchId}/${action}`, { method: 'POST' });
      fetchBatches();
      if (expandedBatchId === batchId) fetchBatchRuns(batchId);
    } catch (err: any) {
      notifyToast('error', `Batch ${action} failed: ${err.message}`);
    }
  };

  const handleCreateNew = () => {
    builderSessionRef.current += 1;
    setEditingAutomation(null);
    setAutoName('New Recruitment Flow');
    setAutoDesc('');
    setAutoCategory('Onboarding');
    setNodes([]);
    setEdges([]);
    setAutoMaxRetries(3);
    setAutoErrorHandling('pause_flow');
    setPublishFeedback(null);
    setSimulation(null);
    // Anchor Auto Save to the fresh draft: it is the baseline, so
    // nothing is dirty (and nothing saves) until the user edits.
    const parts = {
      name: 'New Recruitment Flow',
      description: '',
      category: 'Onboarding',
      nodes: [] as any[],
      edges: [] as any[],
      executionSettings: { maxRetries: 3, errorHandling: 'pause_flow' },
    };
    saveCoordinator.reset({
      sessionId: builderSessionRef.current,
      automationId: null,
      key: draftSnapshotKey(parts),
      ...parts,
    });
    setActiveTab('builder');
  };

  const handleEdit = (auto: AutomationDefinition) => {
    builderSessionRef.current += 1;
    setEditingAutomation(auto);
    setAutoName(auto.name);
    setAutoDesc(auto.description || '');
    setAutoCategory(auto.category);
    setNodes(auto.nodes || []);
    setEdges(auto.edges || []);
    const nextMaxRetries =
      typeof auto.executionSettings?.maxRetries === 'number' ? auto.executionSettings.maxRetries : 3;
    const nextErrorHandling: typeof autoErrorHandling =
      auto.executionSettings?.errorHandling === 'skip_failed_step' ||
      auto.executionSettings?.errorHandling === 'cancel_flow'
        ? auto.executionSettings.errorHandling
        : 'pause_flow';
    setAutoMaxRetries(nextMaxRetries);
    setAutoErrorHandling(nextErrorHandling);
    setPublishFeedback(null);
    setSimulation(null);
    // Anchor Auto Save to the loaded draft (the latest saved state):
    // reopening the editor must not fire a save of an unchanged graph.
    const parts = {
      name: auto.name,
      description: auto.description || '',
      category: auto.category,
      nodes: auto.nodes || [],
      edges: auto.edges || [],
      executionSettings: {
        maxRetries: Math.min(10, Math.max(1, nextMaxRetries || 3)),
        errorHandling: nextErrorHandling,
      },
    };
    saveCoordinator.reset({
      sessionId: builderSessionRef.current,
      automationId: auto.id,
      key: draftSnapshotKey(parts),
      ...parts,
    });
    setActiveTab('builder');
  };

  const currentExecutionSettings = () => ({
    maxRetries: Math.min(10, Math.max(1, autoMaxRetries || 3)),
    errorHandling: autoErrorHandling,
  });

  // Manual Save Draft: an immediate flush of the Auto Save coordinator
  // (cancels any pending debounce instead of double-saving; a clean
  // editor needs no request at all), then today's behaviour.
  const handleSaveDraft = async () => {
    const ok = await saveCoordinator.flush();
    if (ok) {
      fetchAutomations();
      setActiveTab('library');
    } else {
      notifyToast(
        'error',
        `Failed to save automation draft: ${saveCoordinator.getLastError() || 'Save failed'}`
      );
    }
  };

  // Save the builder's current state; returns the automation id.
  // Delegates to the Auto Save coordinator's flush so publish/simulate
  // can never race an in-flight auto save into a duplicate create.
  const saveBuilderDraft = async (): Promise<string> => {
    const ok = await saveCoordinator.flush();
    const id = saveCoordinator.getResolvedId() ?? editingAutomation?.id ?? null;
    if (!ok || !id) {
      throw new Error(saveCoordinator.getLastError() || 'Failed to save automation draft');
    }
    return id;
  };

  const handlePublish = async () => {
    if (!editingAutomation && nodes.length === 0) {
      notifyToast('warning', 'Please add at least one action step before publishing.');
      return;
    }
    setPublishFeedback(null);

    try {
      const autoId = await saveBuilderDraft();

      // Publish snapshot. safeFetch throws with the server's payload on
      // err.data — including the structured validation result, whose
      // ERRORS explain a refusal and whose WARNINGS (I-1) must reach
      // the admin on success: they name the deliberate-but-sharp parts
      // of the flow (stage moves without gate checks, fixed email
      // recipients, unscheduled events).
      const publishData = await safeFetch(`/api/admin/automations/${autoId}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changeNotes: 'Published via Visual Automation Builder' }),
      });

      const warnings: Array<{ message: string }> = publishData?.validation?.warnings || [];
      fetchAutomations();
      if (warnings.length > 0) {
        setPublishFeedback({
          kind: 'warning',
          title: `Published as version ${publishData?.publishedVersion?.version ?? ''} — with ${warnings.length} warning(s) to review:`,
          messages: warnings.map((w) => w.message),
        });
        // Stay in the builder so the warnings are actually read.
      } else {
        setActiveTab('library');
      }
    } catch (err: any) {
      const validation = err?.data?.validation;
      const messages: string[] = [];
      if (Array.isArray(validation?.errors)) {
        for (const e of validation.errors) messages.push(e.message);
      }
      if (messages.length === 0) messages.push(err.message || 'Cannot publish automation');
      setPublishFeedback({ kind: 'error', title: 'This automation was not published:', messages });
    }
  };

  // I-10: a REAL dry run — the server validates presence AND
  // references, then reports per-step what WOULD happen. Nothing is
  // written, sent, or moved.
  const handleSimulate = async () => {
    if (nodes.length === 0) {
      setPublishFeedback({
        kind: 'error',
        title: 'Nothing to simulate:',
        messages: ['Add at least one action step, then run the simulation.'],
      });
      return;
    }
    setSimulating(true);
    setSimulation(null);
    try {
      const autoId = await saveBuilderDraft();
      const data = await safeFetch(`/api/admin/automations/${autoId}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes, edges }),
      });
      setSimulation(data.simulation || null);
      const validation = data.validation;
      if (validation && !validation.valid) {
        setPublishFeedback({
          kind: 'error',
          title: 'Dry run found problems — a real run would fail:',
          messages: (validation.errors || []).map((e: any) => e.message),
        });
      } else if (Array.isArray(validation?.warnings) && validation.warnings.length > 0) {
        setPublishFeedback({
          kind: 'warning',
          title: 'Dry run passed — with warning(s):',
          messages: validation.warnings.map((w: any) => w.message),
        });
      } else {
        setPublishFeedback({
          kind: 'success',
          title: 'Dry run passed — every reference resolves and the flow is valid.',
          messages: [],
        });
      }
      fetchAutomations();
    } catch (err: any) {
      setPublishFeedback({
        kind: 'error',
        title: 'The dry run could not be completed:',
        messages: [err.message || 'Simulation failed'],
      });
    } finally {
      setSimulating(false);
    }
  };

  // I-7: retention — purge terminal runs older than 90 days in this
  // workspace (the server enforces the terminal-only + workspace rules).
  const handlePurgeRuns = async () => {
    if (
      !(await confirmDialog(
        'Delete finished automation runs older than 90 days?\n\nOnly completed, failed, and cancelled runs are deleted. Pending, running, and paused runs are never touched. This cannot be undone.'
      ))
    ) {
      return;
    }
    try {
      const data = await safeFetch('/api/admin/automations/runs/purge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ olderThanDays: 90 }),
      });
      notifyToast('success', `Run history cleaned up: ${data.deleted} old run(s) deleted.`);
      fetchRuns();
    } catch (err: any) {
      notifyToast('error', `Failed to clean up run history: ${err.message}`);
    }
  };

  const handleDuplicate = async (autoId: string) => {
    const label = automations.find(a => a.id === autoId)?.name || 'this automation';
    if (!(await confirmDialog(`Duplicate the automation "${label}"? A separate copy will be created.`))) return;
    try {
      await safeFetch(`/api/admin/automations/${autoId}/duplicate`, {
        method: 'POST',
      });
      fetchAutomations();
    } catch (err: any) {
      console.error('Error duplicating automation:', err);
      notifyToast('error', `Failed to duplicate automation: ${err.message}`);
    }
  };

  const handleArchive = async (autoId: string) => {
    if (!(await confirmDialog('Are you sure you want to archive this automation?'))) return;
    try {
      await safeFetch(`/api/admin/automations/${autoId}`, {
        method: 'DELETE',
      });
      fetchAutomations();
    } catch (err: any) {
      console.error('Error archiving automation:', err);
      notifyToast('error', `Failed to archive automation: ${err.message}`);
    }
  };

  // Filtered Automations
  const filteredAutomations = automations.filter((a) => {
    const matchesSearch =
      a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.automationId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat = categoryFilter === 'all' || a.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesCat && matchesStatus;
  });

  // Calculate KPIs
  const totalAutomations = automations.length;
  const publishedCount = automations.filter((a) => a.status === 'published').length;
  const activeRunsCount = runs.filter((r) => r.status === 'running' || r.status === 'pending').length;
  const completedRunsCount = runs.filter((r) => r.status === 'completed').length;

  // Run history view (I-7): status filter + a stalled indicator. A run
  // still reading "running" whose record has not moved in 15 minutes
  // and whose next step is already due is not progressing — say so
  // instead of letting it blend into the active count forever. (The
  // poller's sweep self-heals genuinely due runs; a persistent stall
  // means the record needs an admin's eyes.)
  const visibleRuns =
    runStatusFilter === 'all' ? runs : runs.filter((r) => r.status === runStatusFilter);
  // S12: failures total for the hard-to-miss chip, and the
  // throughput trend series from the health doc's recentPasses ring.
  const failuresSummary = failuresData?.summary || null;
  const failuresTotal = failuresSummary
    ? (failuresSummary.failedRuns || 0) +
      (failuresSummary.completedWithErrorsRuns || 0) +
      (failuresSummary.failedBatches || 0) +
      (failuresSummary.completedWithErrorsBatches || 0) +
      (failuresSummary.flaggedSends || 0)
    : 0;
  const failureItems: any[] = Array.isArray(failuresData?.items) ? failuresData.items : [];
  const recentPasses: any[] = Array.isArray(engineHealth?.recentPasses)
    ? engineHealth.recentPasses.slice(-16)
    : [];
  const trendMaxProcessed = Math.max(1, ...recentPasses.map((p: any) => p.processedCount || 0));
  const trendMaxQueue = Math.max(1, ...recentPasses.map((p: any) => p.queueDepth || 0));
  const deliveryChipClass = (label: string): string => {
    if (label === 'delivered') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (label === 'bounced' || label === 'failed') return 'bg-rose-50 text-rose-700 border-rose-200';
    if (label === 'complained' || label === 'soft bounced')
      return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-slate-50 text-slate-600 border-slate-200';
  };

  const isStalledRun = (run: AutomationRun): boolean => {
    if (run.status !== 'running') return false;
    const updatedMs = new Date(run.updatedAt || run.createdAt).getTime();
    if (Number.isNaN(updatedMs) || Date.now() - updatedMs < 15 * 60 * 1000) return false;
    return !run.nextScheduledStepAt || new Date(run.nextScheduledStepAt).getTime() <= Date.now();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2.5 rounded-xl bg-brand-primary text-white shadow-md shadow-brand-primary/20">
              <Sparkles size={20} />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-900 tracking-tight">Centralized Automation Engine</h1>
              <p className="text-xs font-semibold text-slate-500">
                Design, test, publish, and schedule reusable recruitment automation flows.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              fetchAutomations();
              fetchRuns();
            }}
            className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
            title="Refresh Data"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-extrabold shadow-md shadow-brand-primary/20 transition-all cursor-pointer"
          >
            <Plus size={16} />
            Create Automation Flow
          </button>
        </div>
      </div>

      {/* KPI Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Total Flows
            </span>
            <span className="text-2xl font-extrabold text-slate-900">{totalAutomations}</span>
          </div>
          <div className="p-3 rounded-xl bg-slate-100 text-slate-600">
            <Layers size={20} />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Active Published
            </span>
            <span className="text-2xl font-extrabold text-emerald-600">{publishedCount}</span>
          </div>
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
            <CheckCircle2 size={20} />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Running Executions
            </span>
            <span className="text-2xl font-extrabold text-blue-600">{activeRunsCount}</span>
          </div>
          <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
            <Clock size={20} />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Completed Runs
            </span>
            <span className="text-2xl font-extrabold text-indigo-600">{completedRunsCount}</span>
          </div>
          <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600">
            <History size={20} />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('library')}
            className={`px-4 py-3 text-xs font-extrabold border-b-2 transition-all cursor-pointer ${
              activeTab === 'library'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Automation Library
          </button>
          <button
            onClick={() => setActiveTab('builder')}
            className={`px-4 py-3 text-xs font-extrabold border-b-2 transition-all cursor-pointer ${
              activeTab === 'builder'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Visual Builder & Canvas
          </button>
          <button
            onClick={() => {
              fetchRuns();
              setActiveTab('history');
            }}
            className={`px-4 py-3 text-xs font-extrabold border-b-2 transition-all cursor-pointer ${
              activeTab === 'history'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Execution History & Scheduler ({runs.length})
            {failuresTotal > 0 && (
              <span className="ml-2 text-[10px] font-black uppercase px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200">
                {failuresTotal} need attention
              </span>
            )}
          </button>
        </div>
      </div>

      {/* TAB 1: AUTOMATION LIBRARY */}
      {activeTab === 'library' && (
        <div className="space-y-4">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-slate-200/80">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or AUT-ID..."
                className="w-full pl-9 pr-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 bg-white"
              >
                <option value="all">All Categories</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Document Processing">Document Processing</option>
                <option value="Invoicing & Finance">Invoicing & Finance</option>
                <option value="Events & Interviews">Events & Interviews</option>
                <option value="Workflow & Stages">Workflow & Stages</option>
                <option value="Batch Operations">Batch Operations</option>
                <option value="General">General</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          {/* Load failure — distinct from a genuinely empty library */}
          {loadError && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
              <span className="flex items-center gap-2">
                <AlertTriangle size={14} />
                Couldn't load automations — {loadError}
              </span>
              <button
                onClick={fetchAutomations}
                className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 font-bold hover:bg-rose-100 transition-colors cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}

          {/* Automations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAutomations.map((auto) => (
              <div
                key={auto.id}
                className="bg-white rounded-2xl border border-slate-200/80 p-5 hover:border-slate-300 transition-all shadow-xs flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="font-mono text-[10px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md">
                      {auto.automationId}
                    </span>
                    <span
                      className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${
                        auto.status === 'published'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-amber-50 text-amber-700 border-amber-200'
                      }`}
                    >
                      {auto.status} · v{auto.currentVersion}
                    </span>
                  </div>

                  <h3 className="text-sm font-extrabold text-slate-900 mb-1 group-hover:text-indigo-600 transition-colors">
                    {auto.name}
                  </h3>
                  <p className="text-xs text-slate-500 line-clamp-2 mb-4">
                    {auto.description || 'No description provided.'}
                  </p>

                  <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 mb-4">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-md">{auto.category}</span>
                    <span>·</span>
                    <span>{auto.nodes?.length || 0} Actions</span>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-xs">
                  <div className="text-[11px] text-slate-500 font-medium">
                    Runs: <strong className="text-slate-700 font-bold">{auto.usageCount || 0}</strong>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(auto)}
                      className="px-2.5 py-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 font-bold transition-colors cursor-pointer"
                    >
                      Edit Flow
                    </button>
                    <button
                      onClick={() => handleDuplicate(auto.id)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Duplicate"
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => handleArchive(auto.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      title="Archive"
                    >
                      <Archive size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredAutomations.length === 0 && !loadError && (
              <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <Sparkles size={32} className="mx-auto text-slate-300 mb-2" />
                <h4 className="text-sm font-bold text-slate-800 mb-1">No Automation Flows Found</h4>
                <p className="text-xs text-slate-500 mb-4">
                  Get started by creating your first reusable recruitment automation flow.
                </p>
                <button
                  onClick={handleCreateNew}
                  className="px-4 py-2 rounded-xl bg-brand-primary text-white text-xs font-bold"
                >
                  Create Flow
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: VISUAL BUILDER & CANVAS */}
      {activeTab === 'builder' && (
        <div className="space-y-4">
          {/* Metadata Controls Header */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Automation Name
                </label>
                <input
                  type="text"
                  value={autoName}
                  onChange={(e) => setAutoName(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-extrabold text-slate-800"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Category
                </label>
                <select
                  value={autoCategory}
                  onChange={(e) => setAutoCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800"
                >
                  <option value="Onboarding">Onboarding</option>
                  <option value="Document Processing">Document Processing</option>
                  <option value="Invoicing & Finance">Invoicing & Finance</option>
                  <option value="Events & Interviews">Events & Interviews</option>
                  <option value="Workflow & Stages">Workflow & Stages</option>
                  <option value="Batch Operations">Batch Operations</option>
                  <option value="General">General</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={autoDesc}
                  onChange={(e) => setAutoDesc(e.target.value)}
                  placeholder="Short operational summary..."
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700"
                />
              </div>
            </div>

            {/* Execution settings (I-5) — honored by the engine at run creation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-slate-100 pt-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Attempts per step (incl. first try)
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={autoMaxRetries}
                  onChange={(e) =>
                    setAutoMaxRetries(Math.min(10, Math.max(1, parseInt(e.target.value) || 3)))
                  }
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  A step that fails with a temporary error is retried until it reaches this many attempts.
                  Permanent mistakes (a deleted template, a missing recipient) always fail on the first attempt.
                </p>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  When a step fails for good
                </label>
                <select
                  value={autoErrorHandling}
                  onChange={(e) => setAutoErrorHandling(e.target.value as typeof autoErrorHandling)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 bg-white"
                >
                  <option value="pause_flow">Stop the run — mark it failed</option>
                  <option value="skip_failed_step">Skip the failed step and continue</option>
                  <option value="cancel_flow">Cancel the run</option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Skipped steps stay visible in the run record with their error, and the run finishes as
                  “completed with errors”.
                </p>
              </div>
            </div>

            {/* I-2 (label): what the Automations grant actually carries */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
              <p className="text-[11px] text-slate-600 leading-snug">
                <strong className="text-slate-800">Authority note:</strong> automations run with the
                portal's full authority — they can send email as this workspace, create Finance quotes and
                invoices, and move applicants between workflow stages. Anyone who can edit automations
                holds all of that through a published flow, so publish only flows you trust.
              </p>
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-xs text-slate-500 font-medium">
                Editing:{' '}
                <strong className="text-slate-800 font-bold">
                  {editingAutomation ? editingAutomation.automationId : 'New Draft'}
                </strong>
              </span>

              <div className="flex items-center gap-3">
                {/* Auto Save status: Saved only when the persisted
                    snapshot equals the current graph. */}
                <span
                  data-testid="autosave-status"
                  role="status"
                  className={`flex items-center gap-1.5 text-[11px] font-bold ${
                    saveStatus === 'failed'
                      ? 'text-rose-600'
                      : saveStatus === 'saving'
                        ? 'text-amber-600'
                        : 'text-emerald-600'
                  }`}
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 size={12} className="animate-spin" />
                      Saving...
                    </>
                  ) : saveStatus === 'failed' ? (
                    <>
                      <AlertTriangle size={12} />
                      Save failed
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={12} />
                      Saved
                    </>
                  )}
                </span>
                {saveStatus === 'failed' && (
                  <button
                    onClick={() => void saveCoordinator.retry()}
                    className="px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 text-xs font-bold text-rose-700 transition-colors cursor-pointer"
                  >
                    Retry
                  </button>
                )}
                <button
                  onClick={handleSaveDraft}
                  className="px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700 transition-colors cursor-pointer"
                >
                  Save Draft
                </button>
                <button
                  onClick={handleSimulate}
                  disabled={simulating}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-xs font-bold text-indigo-700 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <Eye size={14} />
                  {simulating ? 'Simulating…' : 'Simulate (Dry Run)'}
                </button>
                <button
                  onClick={handlePublish}
                  className="px-5 py-2 rounded-xl bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-extrabold shadow-md shadow-brand-primary/20 transition-all cursor-pointer"
                >
                  Validate & Publish
                </button>
              </div>
            </div>
          </div>

          {/* Publish / dry-run feedback (I-1 warnings + validation errors) */}
          {publishFeedback && (
            <div
              className={`rounded-2xl border px-4 py-3 ${
                publishFeedback.kind === 'error'
                  ? 'border-rose-200 bg-rose-50'
                  : publishFeedback.kind === 'warning'
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-emerald-200 bg-emerald-50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <p
                  className={`text-xs font-extrabold ${
                    publishFeedback.kind === 'error'
                      ? 'text-rose-800'
                      : publishFeedback.kind === 'warning'
                        ? 'text-amber-900'
                        : 'text-emerald-800'
                  }`}
                >
                  {publishFeedback.title}
                </p>
                <button
                  onClick={() => setPublishFeedback(null)}
                  className="text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
              {publishFeedback.messages.length > 0 && (
                <ul className="mt-1.5 space-y-1 list-disc list-inside">
                  {publishFeedback.messages.map((m, i) => (
                    <li
                      key={i}
                      className={`text-xs font-medium ${
                        publishFeedback.kind === 'error'
                          ? 'text-rose-700'
                          : publishFeedback.kind === 'warning'
                            ? 'text-amber-800'
                            : 'text-emerald-700'
                      }`}
                    >
                      {m}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Dry-run simulation report (I-10) */}
          {simulation && Array.isArray(simulation.steps) && (
            <div className="bg-white rounded-2xl border border-indigo-100 overflow-hidden">
              <div className="px-4 py-3 bg-indigo-50/60 border-b border-indigo-100 flex items-center justify-between">
                <h4 className="text-xs font-extrabold text-slate-900">
                  Dry Run — what this flow WOULD do (nothing was written, sent, or moved)
                </h4>
                <button aria-label="Close" title="Close"
                  onClick={() => setSimulation(null)}
                  className="text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
                >
                  ✕
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {simulation.steps.map((s: any) => (
                  <div key={s.stepIndex} className="px-4 py-2.5">
                    <p className="text-xs font-bold text-slate-800">
                      Step {s.stepIndex} — {s.label}
                      <span className="text-slate-400 font-semibold"> · {s.delayDescription}</span>
                    </p>
                    {(s.effects || []).map((e: string, i: number) => (
                      <p key={i} className="text-xs text-slate-500 ml-3">
                        → {e}
                      </p>
                    ))}
                  </div>
                ))}
                {simulation.steps.length === 0 && (
                  <p className="px-4 py-3 text-xs text-slate-500">This flow has no action steps.</p>
                )}
              </div>
            </div>
          )}

          {/* Drag & Drop Visual Canvas */}
          <AutomationCanvas
            nodes={nodes}
            edges={edges}
            onChange={(n, e) => {
              setNodes(n);
              setEdges(e);
            }}
          />
        </div>
      )}

      {/* TAB 3: EXECUTION HISTORY & SCHEDULER */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-xs">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div>
              <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
                Automation Run Monitor
                {failuresTotal > 0 && (
                  <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border bg-rose-600 text-white border-rose-600">
                    {failuresTotal} failed — needs attention
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-500">Live execution history and scheduled background jobs.</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={runStatusFilter}
                onChange={(e) => {
                  setRunStatusFilter(e.target.value);
                  fetchRuns({ status: e.target.value });
                }}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 bg-white"
                aria-label="Filter runs by status"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="completed_with_errors">Completed with errors</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={handlePurgeRuns}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                title="Delete finished runs older than 90 days"
              >
                Clear old runs
              </button>
              <button aria-label="Refresh runs" title="Refresh runs"
                onClick={() => fetchRuns()}
                className="p-2 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/50 transition-colors"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {runsError && (
            <div className="m-4 mb-0 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
              <span className="flex items-center gap-2">
                <AlertTriangle size={14} />
                Couldn't load automation runs — {runsError}
              </span>
              <button
                onClick={() => fetchRuns()}
                className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 font-bold hover:bg-rose-100 transition-colors cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}

          {/* Slice 8: Scheduler card — the dispatcher's heartbeat. */}
          <div className="px-6 py-4 border-b border-slate-100 flex flex-wrap items-center gap-x-8 gap-y-2 bg-white">
            <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
              Scheduler
            </h4>
            {engineHealth ? (
              <>
                <span className="text-[11px] text-slate-500">
                  Last pass{' '}
                  <strong className="text-slate-700">
                    {new Date(engineHealth.lastPassAt).toLocaleString([], {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </strong>
                  {Date.now() - new Date(engineHealth.lastPassAt).getTime() > 2 * 60 * 1000 && (
                    <span className="ml-2 text-[10px] font-black uppercase px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                      Stale
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-slate-500">
                  Due <strong className="text-slate-700">{engineHealth.dueCount ?? 0}</strong>
                </span>
                <span className="text-[11px] text-slate-500">
                  Overdue{' '}
                  <strong className={engineHealth.overdueCount ? 'text-rose-600' : 'text-slate-700'}>
                    {engineHealth.overdueCount ?? 0}
                  </strong>
                </span>
                <span className="text-[11px] text-slate-500">
                  Last pass processed{' '}
                  <strong className="text-slate-700">{engineHealth.processedCount ?? 0}</strong>
                  {' · failed '}
                  <strong className="text-slate-700">{engineHealth.failedCount ?? 0}</strong>
                </span>
                <span className="text-[11px] text-slate-500">
                  Queue depth{' '}
                  <strong className="text-slate-700">{engineHealth.queueDepth ?? '—'}</strong>
                </span>
                <span className="text-[11px] text-slate-500">
                  Now due{' '}
                  <strong className="text-slate-700">{engineHealth.dueNowCount ?? '—'}</strong>
                </span>
                <span className="text-[11px] text-slate-500">
                  Oldest waiting{' '}
                  <strong
                    className={
                      engineHealth.oldestWaitingAgeMs > 60 * 60 * 1000
                        ? 'text-rose-600'
                        : 'text-slate-700'
                    }
                  >
                    {formatWaitingAge(engineHealth.oldestWaitingAgeMs)}
                  </strong>
                </span>
                <span className="text-[11px] text-slate-500">
                  Last pass duration{' '}
                  <strong className="text-slate-700">
                    {formatPassDuration(engineHealth.passDurationMs)}
                  </strong>
                </span>
                {recentPasses.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Throughput trend
                    </span>
                    <div className="flex items-end gap-1" aria-label="Throughput trend, last passes">
                      {recentPasses.map((p: any, i: number) => (
                        <div
                          key={i}
                          title={`Processed ${p.processedCount ?? 0} · failed ${p.failedCount ?? 0} · queue ${p.queueDepth ?? 0}`}
                          className="relative h-8 w-2 bg-slate-100 rounded-sm overflow-hidden"
                        >
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-indigo-400"
                            style={{
                              height: `${Math.round(((p.processedCount || 0) / trendMaxProcessed) * 100)}%`,
                            }}
                          />
                          {(p.failedCount || 0) > 0 && (
                            <div
                              className="absolute bottom-0 left-0 right-0 bg-rose-500"
                              style={{
                                height: `${Math.max(12, Math.round(((p.failedCount || 0) / trendMaxProcessed) * 100))}%`,
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-end gap-1" aria-label="Queue depth trend, last passes">
                      {recentPasses.map((p: any, i: number) => (
                        <div
                          key={i}
                          title={`Queue depth ${p.queueDepth ?? 0}`}
                          className="relative h-3 w-2 bg-slate-100 rounded-sm overflow-hidden"
                        >
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-slate-400"
                            style={{
                              height: `${Math.round(((p.queueDepth || 0) / trendMaxQueue) * 100)}%`,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <span className="text-[11px] text-slate-500">
                      Queue: {recentPasses.map((p: any) => p.queueDepth ?? 0).join(' → ')}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <span className="text-[11px] font-semibold text-slate-400">
                No dispatcher pass recorded yet.
              </span>
            )}
          </div>

          {/* S12 / D6: Needs attention — the dead-letter view. Runs
              and batches retry through the existing guarded retry
              endpoints; flagged unknown sends are never resent. */}
          {failuresData && failuresSummary && (
            <div className="px-6 py-4 border-b border-slate-100 bg-white">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                  Needs attention
                </h4>
                <button
                  onClick={() => fetchFailures()}
                  className="p-1.5 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/50 transition-colors"
                  aria-label="Refresh failures"
                >
                  <RefreshCw size={14} />
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Failed runs <strong className="text-rose-600">{failuresSummary.failedRuns ?? 0}</strong>
                {' · '}Completed with errors{' '}
                <strong className="text-slate-700">{failuresSummary.completedWithErrorsRuns ?? 0}</strong>
                {' · '}Failed batches{' '}
                <strong className="text-rose-600">{failuresSummary.failedBatches ?? 0}</strong>
                {' · '}Batches with errors{' '}
                <strong className="text-slate-700">{failuresSummary.completedWithErrorsBatches ?? 0}</strong>
                {' · '}Flagged sends{' '}
                <strong className="text-amber-700">{failuresSummary.flaggedSends ?? 0}</strong>
              </p>
              {Array.isArray(failuresSummary.reasons) && failuresSummary.reasons.length > 0 && (
                <div className="mt-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Top failure reasons
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {failuresSummary.reasons.map((r: any, i: number) => (
                      <li key={i} className="text-[11px] text-slate-600">
                        {r.reason} <strong className="text-slate-800">× {r.count}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(failuresSummary.topAutomations) && failuresSummary.topAutomations.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-2">
                  Most failing flows:{' '}
                  {failuresSummary.topAutomations
                    .map((a: any) => `${a.automationName || a.automationId} (${a.count})`)
                    .join(' · ')}
                </p>
              )}
              <div className="divide-y divide-slate-100 mt-2 rounded-xl border border-slate-100">
                {failureItems.map((item: any) => (
                  <div key={`${item.kind}-${item.id}`} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">
                        {item.kind === 'send_unknown'
                          ? `${item.subject || 'Email'} → ${item.to || ''}`
                          : item.automationName || item.id}
                        <span className="ml-2 text-[10px] font-black uppercase px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
                          {item.kind === 'send_unknown' ? 'send unknown' : item.kind}
                        </span>
                        {item.status && (
                          <span className="ml-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200">
                            {String(item.status).replace(/_/g, ' ')}
                          </span>
                        )}
                      </p>
                      {item.errorSummary && (
                        <p className="text-[11px] text-rose-600 truncate">{safePiiText(item.errorSummary)}</p>
                      )}
                      {item.at && (
                        <p className="text-[11px] text-slate-500">
                          {new Date(item.at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {item.kind === 'send_unknown' && (
                        <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                          Flagged — delivery unknown
                        </span>
                      )}
                      {item.retryable && (
                        <button
                          onClick={() => handleFailureRetry(item)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[11px] font-bold transition-all cursor-pointer"
                        >
                          <RotateCcw size={11} /> Retry
                        </button>
                      )}
                      {(item.kind === 'run' || (item.kind === 'send_unknown' && item.runDocId)) && (
                        <button
                          onClick={() =>
                            openRunByDocId(item.kind === 'run' ? item.id : item.runDocId, item.runId)
                          }
                          className="px-2.5 py-1 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-[11px] font-bold transition-all cursor-pointer"
                        >
                          Open run
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {failureItems.length === 0 && (
                  <p className="px-4 py-3 text-[11px] font-semibold text-slate-400">
                    No failed runs, batches, or flagged sends right now.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* S12: Email delivery — per-recipient rows from emailLogs.
              Labels are the server's D2-honest deliveryLabel values:
              "sent (provider accepted)" until a real delivery event
              lands, never "delivered" on acceptance alone. */}
          <div className="px-6 py-4 border-b border-slate-100 bg-white">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                Email delivery
              </h4>
              <button
                onClick={() => fetchDeliveries()}
                className="p-1.5 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/50 transition-colors"
                aria-label="Refresh deliveries"
              >
                <RefreshCw size={14} />
              </button>
            </div>
            <div className="divide-y divide-slate-100 mt-2 rounded-xl border border-slate-100">
              {deliveries.map((d: any) => (
                <div key={d.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate">
                      {safePiiText(d.subject, 'Email')} <span className="text-slate-400 font-semibold">→ {safePiiText(d.to, '')}</span>
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {d.provider || 'provider unknown'}
                      {d.messageId ? ` · ${d.messageId}` : ''}
                      {d.sentAt
                        ? ` · sent ${new Date(d.sentAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}`
                        : ''}
                      {d.deliveryEventAt
                        ? ` · event ${new Date(d.deliveryEventAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}`
                        : ''}
                      {d.bounceReason ? ` · ${d.bounceReason}` : ''}
                    </p>
                  </div>
                  <span
                    className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border shrink-0 ${deliveryChipClass(d.deliveryLabel || '')}`}
                  >
                    {d.deliveryLabel}
                  </span>
                </div>
              ))}
              {deliveries.length === 0 && (
                <p className="px-4 py-3 text-[11px] font-semibold text-slate-400">
                  No email deliveries recorded yet.
                </p>
              )}
            </div>
            {deliveriesNextCursor && (
              <button
                onClick={() => fetchDeliveries({ append: true, cursor: deliveriesNextCursor })}
                className="mt-2 px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                Load more deliveries
              </button>
            )}
          </div>

          {/* Slice 7: batch layer — one row per fan-out submission,
              drill-down into its runs. */}
          <div className="border-b border-slate-100">
            <div className="px-6 pt-4 pb-2 flex items-center justify-between">
              <h4 className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                Batch executions
              </h4>
              <button
                onClick={() => fetchBatches()}
                className="p-1.5 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/50 transition-colors"
                aria-label="Refresh batches"
              >
                <RefreshCw size={14} />
              </button>
            </div>
            {batchesError && (
              <p className="px-6 pb-3 text-xs font-semibold text-rose-600">
                Couldn't load batches — {batchesError}
              </p>
            )}
            <div className="divide-y divide-slate-100">
              {batches.map((batch) => {
                const isExpanded = expandedBatchId === batch.id;
                const terminal = ['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(
                  batch.status
                );
                return (
                  <div key={batch.id}>
                    <div
                      onClick={() => toggleBatch(batch.id)}
                      className="px-6 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <span className="font-mono text-xs font-black text-violet-600 bg-violet-50 border border-violet-100 px-2.5 py-1 rounded-lg">
                          {batch.batchExecutionId || batch.id}
                        </span>
                        <div className="min-w-0">
                          <h5 className="text-xs font-extrabold text-slate-900 flex items-center gap-2">
                            {batch.automationName}
                            <span className="text-[11px] text-slate-500 font-semibold">
                              v{batch.automationVersion}
                            </span>
                            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
                              {batch.source}
                            </span>
                          </h5>
                          <p className="text-[11px] text-slate-500">
                            Total {batch.totalApplicants} · Queued {batch.queuedApplicants} ·
                            Running {batch.processingApplicants} · Completed{' '}
                            {batch.completedApplicants} · Failed {batch.failedApplicants}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-xs">
                          <span className="text-[11px] text-slate-500 font-semibold block">
                            Created
                          </span>
                          <strong className="text-slate-700 font-bold">
                            {new Date(batch.createdAt).toLocaleString([], {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </strong>
                        </div>
                        <span
                          className={`text-[10px] font-black uppercase px-3 py-1 rounded-full border ${
                            batch.status === 'completed'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : batch.status === 'processing' || batch.status === 'queued'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : batch.status === 'completed_with_errors' || batch.status === 'paused'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}
                        >
                          {batch.status.replace(/_/g, ' ')}
                        </span>
                        <div
                          className="flex items-center gap-1.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {(batch.status === 'queued' || batch.status === 'processing') && (
                            <button
                              onClick={() => handleBatchAction(batch.id, 'pause')}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-[11px] font-bold transition-all"
                            >
                              <Pause size={11} /> Pause
                            </button>
                          )}
                          {batch.status === 'paused' && (
                            <button
                              onClick={() => handleBatchAction(batch.id, 'resume')}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 text-[11px] font-bold transition-all"
                            >
                              <Play size={11} /> Resume
                            </button>
                          )}
                          {(batch.status === 'failed' || batch.status === 'completed_with_errors') && (
                            <button
                              onClick={() => handleBatchAction(batch.id, 'retry')}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[11px] font-bold transition-all"
                            >
                              <RotateCcw size={11} /> Retry failed
                            </button>
                          )}
                          {!terminal && (
                            <button
                              onClick={() => handleBatchAction(batch.id, 'cancel')}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-[11px] font-bold transition-all"
                            >
                              <Ban size={11} /> Cancel
                            </button>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronDown size={16} className="text-slate-400" />
                        ) : (
                          <ChevronRight size={16} className="text-slate-400" />
                        )}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-6 pb-4">
                        <p className="text-[11px] text-slate-500 mb-2">
                          Reused {batch.reusedApplicants} · Skipped {batch.skippedApplicants} ·
                          Terminal-blocked {batch.terminalBlockedApplicants}
                        </p>
                        <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                          {batchRuns.map((run) => (
                            <div
                              key={run.id}
                              onClick={() => {
                                setSelectedRun(run);
                                setIsRunModalOpen(true);
                              }}
                              className="px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-[11px] font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-lg">
                                  {run.runId}
                                </span>
                                <span className="text-xs text-slate-600">
                                  {safePiiText(run.applicantName)} ({run.applicationCustomId})
                                </span>
                              </div>
                              <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
                                {run.status.replace(/_/g, ' ')}
                              </span>
                            </div>
                          ))}
                          {batchRuns.length === 0 && (
                            <p className="px-4 py-3 text-[11px] font-semibold text-slate-400">
                              No runs loaded for this batch yet.
                            </p>
                          )}
                        </div>
                        {batchRunsNextCursor && (
                          <button
                            onClick={() =>
                              fetchBatchRuns(batch.id, {
                                append: true,
                                cursor: batchRunsNextCursor,
                              })
                            }
                            className="mt-2 px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                          >
                            Load more runs
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {batches.length === 0 && !batchesError && (
                <p className="px-6 py-4 text-[11px] font-semibold text-slate-400">
                  No batch executions yet — batches appear here when an automation is assigned
                  to one or more applicants.
                </p>
              )}
            </div>
            {batchesNextCursor && (
              <div className="px-6 py-3">
                <button
                  onClick={() => fetchBatches({ append: true, cursor: batchesNextCursor })}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  Load more batches
                </button>
              </div>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            {visibleRuns.map((run) => (
              <div
                key={run.id}
                onClick={() => {
                  setSelectedRun(run);
                  setIsRunModalOpen(true);
                }}
                className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <span className="font-mono text-xs font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-lg">
                    {run.runId}
                  </span>
                  <div>
                    <h4 className="text-xs font-extrabold text-slate-900 flex items-center gap-2">
                      {run.automationName}
                      <span className="text-[11px] text-slate-500 font-semibold">
                        v{run.automationVersion}
                      </span>
                    </h4>
                    <p className="text-xs text-slate-500">
                      Applicant: <strong className="text-slate-800">{safePiiText(run.applicantName)}</strong> (
                      {run.applicationCustomId})
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right text-xs">
                    <span className="text-[11px] text-slate-500 font-semibold block">Started</span>
                    <strong className="text-slate-700 font-bold">
                      {new Date(run.createdAt).toLocaleDateString()}
                    </strong>
                  </div>

                  <span
                    className={`text-[10px] font-black uppercase px-3 py-1 rounded-full border ${
                      run.status === 'completed'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : run.status === 'running'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : run.status === 'completed_with_errors' || run.status === 'paused'
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-rose-50 text-rose-700 border-rose-200'
                    }`}
                  >
                    {run.status.replace(/_/g, ' ')}
                  </span>

                  {isStalledRun(run) && (
                    <span
                      className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
                      title="This run has shown no progress for over 15 minutes although its next step is due. Open it to inspect, or pause/resume it."
                    >
                      Stalled?
                    </span>
                  )}

                  <ChevronRight size={16} className="text-slate-400" />
                </div>
              </div>
            ))}

            {visibleRuns.length === 0 && !runsError && (
              <div className="p-12 text-center text-slate-500 text-xs font-bold">
                {runs.length === 0
                  ? 'No automation execution runs recorded yet.'
                  : 'No runs match the selected status filter.'}
              </div>
            )}
          </div>
          {runsNextCursor && (
            <div className="px-6 py-3 border-t border-slate-100">
              <button
                onClick={() => fetchRuns({ append: true, cursor: runsNextCursor })}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                Load more runs
              </button>
            </div>
          )}
        </div>
      )}

      {/* Execution Detail Modal */}
      <AutomationRunDetailModal
        run={selectedRun}
        isOpen={isRunModalOpen}
        onClose={() => setIsRunModalOpen(false)}
        onRefresh={() => {
          // Slice 8: refresh the lists in place — the modal stays
          // open and refetches its own run.
          fetchRuns();
          fetchBatches();
        }}
      />
    </div>
  );
};
